import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  Camera, X, Plus, Save, MapPin, Briefcase, Baby, 
  Heart, HelpCircle, MessageCircle, Calendar, RefreshCw,
  Trash2, Image, Sparkles
} from "lucide-react";
import { 
  PHASES,
  WORKING_STATUS_OPTIONS, 
  HELP_TOPICS, 
  DEFAULT_INTERESTS,
  PHASES_WITH_CHILDREN_INFO,
  getChildrenInfoOptionsForPhase,
  getPhaseLabel,
  getWorkingStatusLabel,
  getChildrenInfoLabel
} from "@/constants/profileOptions";

const EditProfile = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    bio: "",
    location: "",
    phase: "",
    interests: [],
    photos: [],
    looking_for_help: [],
    willing_to_help: [],
    working_status: "",
    children_info: "",
    due_date: "",
    child_birthday: "",
  });

  useEffect(() => {
    if (user) {
      setFormData({
        bio: user.bio || "",
        location: user.location || "",
        phase: user.phase || "",
        interests: user.interests || [],
        photos: user.photos || [],
        looking_for_help: user.looking_for_help || [],
        willing_to_help: user.willing_to_help || [],
        working_status: user.working_status || "",
        children_info: user.children_info || "",
        due_date: user.due_date || "",
        child_birthday: user.child_birthday || "",
      });
    }
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (formData.photos.length >= 3) {
      toast.error("Maximum 3 photos allowed");
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/users/upload`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, response.data.url]
      }));
      toast.success("Photo uploaded successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const toggleTopic = (topic, field) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(topic)) {
        return { ...prev, [field]: current.filter(t => t !== topic) };
      } else {
        return { ...prev, [field]: [...current, topic] };
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, formData, { withCredentials: true });
      toast.success("Profile updated successfully");
      if (refreshUser) refreshUser();
      navigate("/profile");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <TopHeader title="Edit Profile" />
      
      <main className="pt-20 pb-24 px-4 max-w-2xl mx-auto">
        <div className="space-y-6">
          
          {/* Profile Picture & Photos */}
          <Card className="bg-white border-sage/20">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <Camera className="w-5 h-5 text-sage" />
                Photos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Profile Picture */}
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-sage/20 text-sage-dark text-xl">
                    {user?.name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm text-sage-darker font-medium">Profile Picture</p>
                  <p className="text-xs text-muted-foreground">From your Google account</p>
                </div>
              </div>

              {/* Additional Photos */}
              <div>
                <Label className="text-sage-darker mb-2 block">Additional Photos (up to 3)</Label>
                <div className="grid grid-cols-3 gap-3">
                  {formData.photos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-sage/10">
                      <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 h-6 w-6 p-0"
                        onClick={() => removePhoto(idx)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  
                  {formData.photos.length < 3 && (
                    <label className={`aspect-square rounded-lg border-2 border-dashed border-sage/30 flex flex-col items-center justify-center cursor-pointer hover:border-sage/50 hover:bg-sage/5 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                      {uploading ? (
                        <RefreshCw className="w-6 h-6 text-sage animate-spin" />
                      ) : (
                        <>
                          <Plus className="w-6 h-6 text-sage" />
                          <span className="text-xs text-sage-dark mt-1">Add Photo</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="bg-white border-sage/20">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-sage-darker">Basic Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sage-darker">Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell others about yourself..."
                  className="bg-cream border-sage/20 min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sage-darker flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="City, Country"
                  className="bg-cream border-sage/20"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sage-darker flex items-center gap-2">
                  <Baby className="w-4 h-4" />
                  Journey Phase
                </Label>
                <Select 
                  value={formData.phase} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, phase: v }))}
                >
                  <SelectTrigger className="bg-cream border-sage/20">
                    <SelectValue placeholder="Select your phase" />
                  </SelectTrigger>
                  <SelectContent>
                    {PHASES.map((phase) => (
                      <SelectItem key={phase.value} value={phase.value}>
                        {phase.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date for Pregnant */}
              {formData.phase === 'pregnant' && (
                <div className="space-y-2">
                  <Label className="text-sage-darker flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Due Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="bg-cream border-sage/20"
                  />
                </div>
              )}

              {/* Child Birthday for Postpartum */}
              {formData.phase === 'postpartum' && (
                <div className="space-y-2">
                  <Label className="text-sage-darker flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Baby&apos;s Birthday
                  </Label>
                  <Input
                    type="date"
                    value={formData.child_birthday}
                    onChange={(e) => setFormData(prev => ({ ...prev, child_birthday: e.target.value }))}
                    className="bg-cream border-sage/20"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Life Details */}
          <Card className="bg-white border-sage/20">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-sage-darker">Life Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sage-darker flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Working Status
                </Label>
                <Select 
                  value={formData.working_status} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, working_status: v }))}
                >
                  <SelectTrigger className="bg-cream border-sage/20">
                    <SelectValue placeholder="Select your status" />
                  </SelectTrigger>
                  <SelectContent>
                    {WORKING_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Children Info - Only show for relevant phases */}
              {PHASES_WITH_CHILDREN_INFO.includes(formData.phase) && getChildrenInfoOptionsForPhase(formData.phase).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sage-darker flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    {formData.phase === 'ttc' ? 'Your Situation' : 'Children'}
                  </Label>
                  <Select 
                    value={formData.children_info} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, children_info: v }))}
                  >
                    <SelectTrigger className="bg-cream border-sage/20">
                      <SelectValue placeholder="Select your situation" />
                    </SelectTrigger>
                    <SelectContent>
                      {getChildrenInfoOptionsForPhase(formData.phase).map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Looking For Help */}
          <Card className="bg-white border-sage/20">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-sage" />
                Looking for Help With
              </CardTitle>
              <p className="text-sm text-muted-foreground">Select topics you would like support or advice on</p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {HELP_TOPICS.map(topic => (
                  <Badge
                    key={topic}
                    variant={formData.looking_for_help?.includes(topic) ? "default" : "outline"}
                    className={`cursor-pointer transition-colors ${
                      formData.looking_for_help?.includes(topic)
                        ? "bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-200"
                        : "border-sage/30 text-sage-dark hover:bg-sage/10"
                    }`}
                    onClick={() => toggleTopic(topic, 'looking_for_help')}
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Willing to Help */}
          <Card className="bg-white border-sage/20">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-sage" />
                Willing to Help Others With
              </CardTitle>
              <p className="text-sm text-muted-foreground">Select topics you can offer guidance on</p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {HELP_TOPICS.map(topic => (
                  <Badge
                    key={topic}
                    variant={formData.willing_to_help?.includes(topic) ? "default" : "outline"}
                    className={`cursor-pointer transition-colors ${
                      formData.willing_to_help?.includes(topic)
                        ? "bg-green-100 text-green-700 border-green-200 hover:bg-green-200"
                        : "border-sage/30 text-sage-dark hover:bg-sage/10"
                    }`}
                    onClick={() => toggleTopic(topic, 'willing_to_help')}
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-sage text-white hover:bg-sage-dark py-6 text-lg"
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default EditProfile;
